import { CategorizationMap } from "portal/utils/categoryCollection/category";
import { CategorizedImageExpandedView } from "./CategorizedImageExpandedView";
import {
  CategorizedThumbnails,
  SuggestedPlaceholders,
} from "./CategorizedThumbnails";
import { Dimensions } from "portal/utils/categoryCollection/image";
import { IconButton, Tooltip } from "@mui/material";
import { RadioChipOption } from "portal/components/RadioChips";
import { SessionStatus } from "protos/veselka/prediction_point";
import { theme } from "portal/theme/theme";
import { useCategorizedImageData } from "./useCategorizedImageData";
import { useExpandedImageNavigation } from "./useExpandedImageNavigation";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from "portal/state/store";
import MoreIcon from "@mui/icons-material/Add";
import React, { FC, useMemo } from "react";

interface CategorizedBarProps {
  categoryOptions: RadioChipOption[];
  categorizationMap: CategorizationMap;
  categoryId?: string;
  predictionPointIds: string[];
  dimensions: Dimensions;
  onRemove?: (predictionPointId: string) => void;
  onClassify?: (predictionPointId: string, categoryId: string) => void;
  suggestedMin?: number;
  hideCategoryLabel?: boolean;
  session?: {
    processing: boolean;
    stale: boolean;
    status?: SessionStatus;
  };
}
export const CategorizedBar: FC<CategorizedBarProps> = ({
  categoryOptions,
  categorizationMap,
  categoryId,
  predictionPointIds = [],
  dimensions,
  onRemove,
  onClassify,
  suggestedMin,
  hideCategoryLabel = false,
  session,
}) => {
  const { t } = useTranslation();
  const userPreferences = useUserPreferences();

  const {
    expandedImage,
    setExpandedImage,
    orderedCombinedData,
    suggestedAdditions,
    loadMoreRef,
    hasMore,
    loadMore,
  } = useCategorizedImageData(predictionPointIds, categoryId, suggestedMin);

  const { onNext, onBack } = useExpandedImageNavigation(
    orderedCombinedData,
    expandedImage,
    setExpandedImage,
    categoryId
  );

  const category = useMemo(() => {
    return categoryOptions.find((c) => c.id === categoryId);
  }, [categoryId, categoryOptions]);

  return (
    <>
      <CategorizedImageExpandedView
        expandedImage={expandedImage}
        setExpandedImage={setExpandedImage}
        onNext={onNext}
        onBack={onBack}
        categoryOptions={categoryOptions}
        categorizationMap={categorizationMap}
        onClassify={onClassify}
        session={session}
      />
      <div className="flex flex-col">
        {!hideCategoryLabel && (
          <p
            className="px-1 py-0.5 m-0 w-fit text-xs hidden md:block"
            style={{
              backgroundColor:
                category?.color?.bg ?? theme.colors.carbon.gray.light,
              color: category?.color?.text ?? theme.colors.white,
            }}
          >
            {category?.name.toLocaleUpperCase()}
          </p>
        )}
        <div
          className="overflow-x-auto flex gap-1 whitespace-nowrap border-1 border-solid p-1"
          style={{
            borderColor: category?.color?.bg ?? theme.colors.carbon.gray.light,
            minHeight: `${dimensions.height + 12}px`,
          }}
        >
          <CategorizedThumbnails
            dimensions={dimensions}
            images={orderedCombinedData}
            showLabels={userPreferences.categoryCollection.showLabels}
            onRemove={(d) => {
              onRemove?.(d);
            }}
            onDoubleClick={(i, data) => {
              setExpandedImage({
                index: i,
                data,
                editableCategoryId: categoryId,
              });
            }}
          />
          {suggestedAdditions > 0 && (
            <SuggestedPlaceholders
              count={suggestedAdditions}
              dimensions={dimensions}
            />
          )}
          {hasMore && (
            <div className="flex items-center" ref={loadMoreRef}>
              <Tooltip title={t("utils.lists.loadMore")}>
                <span>
                  <IconButton
                    className="text-carbon-orange"
                    size="small"
                    onClick={loadMore}
                    aria-label={t("utils.lists.loadMore")}
                  >
                    <MoreIcon className="text-inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
